import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';
import {useAuth} from './AuthContext';

export interface SessionContextValue {
  resetTimer: () => void;
  getRemainingTime: () => number;
  getWarningTime: () => number;
  setTimeout: (newTimeout: number) => void;
  getSecurityLogs: () => any[];
  secureLogout: () => void;
}

const SessionContext = createContext<SessionContextValue | undefined>(
  undefined,
);

// Inactivity-based auto-logout is disabled. Firebase Auth refresh tokens keep
// the user signed in indefinitely (subject to Firebase's own rotation/revocation
// rules). To re-enable an inactivity timeout, set
// REACT_APP_INACTIVITY_TIMEOUT_ENABLED=true and optionally
// REACT_APP_SESSION_TIMEOUT=<minutes>.
const INACTIVITY_TIMEOUT_ENABLED =
  process.env.REACT_APP_INACTIVITY_TIMEOUT_ENABLED === 'true';

// Default timeout only applies when the feature is explicitly enabled.
const DEFAULT_TIMEOUT = 7 * 24 * 60; // 7 days (10,080 minutes)
const SESSION_TIMEOUT = process.env.REACT_APP_SESSION_TIMEOUT
  ? parseInt(process.env.REACT_APP_SESSION_TIMEOUT, 10)
  : DEFAULT_TIMEOUT;

// Convert minutes to milliseconds
const SESSION_TIMEOUT_MS = SESSION_TIMEOUT * 60 * 1000;

// Get warning time from environment variables or use default (2 minutes)
const DEFAULT_WARNING_TIME = 2; // 2 minutes
const WARNING_TIMEOUT = process.env.REACT_APP_WARNING_TIMEOUT
  ? parseInt(process.env.REACT_APP_WARNING_TIMEOUT, 10)
  : DEFAULT_WARNING_TIME;

// Convert warning minutes to milliseconds
const WARNING_TIMEOUT_MS = WARNING_TIMEOUT * 60 * 1000;

export function useSession(): SessionContextValue {
  // The app always renders inside SessionProvider.
  return useContext(SessionContext) as SessionContextValue;
}

// Storage key for persisting last activity time
const LAST_ACTIVITY_STORAGE_KEY = 'calliope_last_activity';

// Security logging function
const logSecurityEvent = (
  eventType: string,
  details: Record<string, unknown> = {},
) => {
  const timestamp = new Date().toISOString();
  const logData = {
    timestamp,
    eventType,
    userAgent: navigator.userAgent,
    sessionStartTime: sessionStorage.getItem('session_start_time'),
    ...details,
  };

  // Store security events in session storage for audit purposes
  const existingLogs = JSON.parse(
    sessionStorage.getItem('security_logs') || '[]',
  );
  existingLogs.push(logData);
  // Keep only last 50 events to prevent storage overflow
  if (existingLogs.length > 50) {
    existingLogs.splice(0, existingLogs.length - 50);
  }
  sessionStorage.setItem('security_logs', JSON.stringify(existingLogs));
};

export function SessionProvider({
  children,
  timeout = SESSION_TIMEOUT_MS,
}: {
  children: React.ReactNode;
  timeout?: number;
}) {
  const {logout, currentUser} = useAuth();
  const [lastActivity, setLastActivity] = useState(() => {
    // Load last activity time from localStorage to persist across refreshes
    try {
      const stored = localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
      if (stored) {
        const storedTime = parseInt(stored, 10);
        // Only use stored time if it's valid and not too old
        if (!isNaN(storedTime) && storedTime > 0) {
          return storedTime;
        }
      }
    } catch (e) {
      console.warn('Failed to load last activity from localStorage:', e);
    }
    return Date.now();
  });
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [warningTime] = useState(WARNING_TIMEOUT_MS);
  const sessionStartTimeRef = useRef<string | null>(null);
  const [sessionInitialized, setSessionInitialized] = useState(false);

  // Initialize session tracking
  useEffect(() => {
    if (currentUser && !sessionStartTimeRef.current) {
      // Check if there's already a session in storage (for navigation between pages)
      const existingSessionStart = sessionStorage.getItem('session_start_time');
      const existingAuth = sessionStorage.getItem('user_authenticated');

      if (existingSessionStart && existingAuth) {
        // Reuse existing session
        sessionStartTimeRef.current = existingSessionStart;
        logSecurityEvent('SESSION_RESUMED', {
          userId: currentUser.uid,
          email: currentUser.email,
          originalSessionStart: existingSessionStart,
        });
      } else {
        // Create new encounter
        const sessionStartTime = new Date().toISOString();
        sessionStartTimeRef.current = sessionStartTime;
        sessionStorage.setItem('session_start_time', sessionStartTime);
        sessionStorage.setItem('user_authenticated', 'true');

        logSecurityEvent('SESSION_STARTED', {
          userId: currentUser.uid,
          email: currentUser.email,
        });
      }

      // Mark session as initialized
      setSessionInitialized(true);
    } else if (currentUser && sessionStartTimeRef.current) {
      // Session was already initialized (e.g., on navigation)
      setSessionInitialized(true);
    }
  }, [currentUser]);

  // Reset the timer whenever there is user activity
  const resetTimer = () => {
    const now = Date.now();
    setLastActivity(now);
    // Persist to localStorage so timeout continues across page refreshes
    try {
      localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, now.toString());
    } catch (e) {
      console.warn('Failed to save last activity to localStorage:', e);
    }
    logSecurityEvent('USER_ACTIVITY', {activityTime: new Date().toISOString()});
  };

  // Enhanced logout function with security logging
  const secureLogout = (reason: string = 'MANUAL_LOGOUT') => {
    logSecurityEvent('LOGOUT_INITIATED', {reason});

    // Clear session data
    sessionStorage.removeItem('session_start_time');
    sessionStorage.removeItem('user_authenticated');

    // Clear last activity time
    localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);

    // Let Firebase Auth handle its own persistence cleanup via logout().
    // Manually wiping localStorage keys matching 'firebase'/'auth'/'token'
    // destroys Firebase Auth's persisted state and causes downstream failures
    // (e.g. Storage uploads rejected because the auth token is gone).
    logout();
  };

  // Handle visibility changes (tab switching)
  // Note: We no longer log out on page unload/refresh since there's no reliable way
  // to distinguish between closing the browser and refreshing. The inactivity
  // timeout handles session security, and Firebase persists auth across refreshes.
  useEffect(() => {
    if (!currentUser || !sessionInitialized) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        logSecurityEvent('PAGE_HIDDEN', {
          reason: 'User switched tab or minimized browser',
        });
      } else if (document.visibilityState === 'visible') {
        logSecurityEvent('PAGE_VISIBLE', {
          reason: 'User returned to tab',
        });
        resetTimer(); // Reset activity timer when user returns
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentUser, sessionInitialized]);

  // Set up the timer to check for inactivity (only after session is initialized)
  useEffect(() => {
    if (!currentUser || !sessionInitialized) return;
    if (!INACTIVITY_TIMEOUT_ENABLED) return;

    // Function to check for inactivity and logout if needed
    const checkInactivity = () => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;

      if (timeSinceLastActivity >= timeout) {
        // Log the user out if they've been inactive for longer than the timeout
        logSecurityEvent('AUTOMATIC_LOGOUT', {
          reason: 'Inactivity timeout exceeded',
          inactiveTimeMs: timeSinceLastActivity,
          timeoutSettingMs: timeout,
        });
        console.debug('logging out - checkInactivity');
        secureLogout('INACTIVITY_TIMEOUT');
      } else {
        // Schedule the next check
        clearTimeout(timeoutRef.current ?? undefined);
        timeoutRef.current = setTimeout(
          checkInactivity,
          timeout - timeSinceLastActivity,
        );
      }
    };

    // Clear any existing timeout
    clearTimeout(timeoutRef.current ?? undefined);

    // Set a new timeout
    timeoutRef.current = setTimeout(checkInactivity, timeout);

    // Cleanup on unmount
    return () => {
      clearTimeout(timeoutRef.current ?? undefined);
    };
    // The inactivity timer must reset only on the deps above.
    // `secureLogout` is recreated each render; listing it would reset
    // the timer on unrelated provider re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastActivity, timeout, currentUser, sessionInitialized]);

  // Set up event listeners for user activity (only after session is initialized)
  useEffect(() => {
    if (!currentUser || !sessionInitialized) return;
    if (!INACTIVITY_TIMEOUT_ENABLED) return;

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown',
    ];

    // Throttle the reset timer function to avoid too many state updates
    let throttleTimeout: ReturnType<typeof setTimeout> | null = null;
    const throttledResetTimer = () => {
      if (!throttleTimeout) {
        throttleTimeout = setTimeout(() => {
          resetTimer();
          throttleTimeout = null;
        }, 1000); // Update at most once per second
      }
    };

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, throttledResetTimer);
    });

    // Initial reset
    resetTimer();

    // Clean up event listeners on unmount
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, throttledResetTimer);
      });
      clearTimeout(throttleTimeout ?? undefined);
    };
  }, [currentUser, sessionInitialized]);

  // Check for session validity on mount (TEMPORARILY DISABLED FOR DEBUGGING)
  useEffect(() => {
    if (currentUser && sessionInitialized) {
      // TEMPORARILY DISABLED - just log that we would validate
      logSecurityEvent('SESSION_VALIDATION_SKIPPED', {
        reason: 'Session validation temporarily disabled for debugging',
      });
    }
  }, [currentUser, sessionInitialized]);

  // Log session timeout settings on mount (for debugging and audit)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      if (INACTIVITY_TIMEOUT_ENABLED) {
        console.debug(`Session timeout set to: ${timeout / 60000} minutes`);
        console.debug(
          `Warning will show ${warningTime / 60000} minutes before timeout`,
        );
      } else {
        console.debug(
          'Inactivity timeout is disabled; users remain signed in until explicit logout.',
        );
      }
    }

    logSecurityEvent('SESSION_CONFIG_LOADED', {
      inactivityTimeoutEnabled: INACTIVITY_TIMEOUT_ENABLED,
      timeoutMinutes: timeout / 60000,
      warningMinutes: warningTime / 60000,
    });
  }, [timeout, warningTime]);

  const value: SessionContextValue = {
    resetTimer,
    // Expose the remaining time until logout (in milliseconds).
    // When the inactivity timeout is disabled, return Infinity so any consumer
    // (e.g. SessionTimeoutWarning) treats the session as never expiring.
    getRemainingTime: () => {
      if (!INACTIVITY_TIMEOUT_ENABLED) return Infinity;
      return timeout - (Date.now() - lastActivity);
    },
    // Expose warning time for components that need it
    getWarningTime: () => warningTime,
    // Allow consumer components to manually set the timeout (in milliseconds)
    setTimeout: (newTimeout: number) => {
      if (typeof newTimeout === 'number' && newTimeout > 0) {
        timeout = newTimeout;
        resetTimer();
        logSecurityEvent('TIMEOUT_CHANGED', {
          newTimeoutMinutes: newTimeout / 60000,
        });
      }
    },
    // Expose security logs for admin/debugging purposes
    getSecurityLogs: () => {
      return JSON.parse(sessionStorage.getItem('security_logs') || '[]');
    },
    // Manual secure logout
    secureLogout: () => secureLogout('MANUAL_LOGOUT'),
  };

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}
